/* tslint:disable:no-inferrable-types no-trailing-whitespace import-spacing typedef-whitespace align whitespace quotemark */

import { DocumentWindow }           from './document.window';
import { DocumentWindowManager }    from './document.window.manager';
import { Logger }                   from '@/util/logger';
import {basename, errorAsText} from '@/util/util';
import { BlobService }              from '@/services/blob.service';
import { AuthenticationService}     from '@/services/authentication.service';
import { IdeService}                from '@/services/ide.service';
import { StringLineViewerMetadata } from './string.line.viewer.metadata';

import { StringViewerComponent    } from '@/components/string.viewer.component';

import * as $                       from 'jquery';
import 'gasparesganga-jquery-loading-overlay'; /* https://gasparesganga.com/labs/jquery-loading-overlay/ */

import { first                   } from "rxjs/operators";
import { forkJoin                } from 'rxjs';

import {
  ComponentFactoryResolver,
  ViewContainerRef,
  EmbeddedViewRef
}                                  from '@angular/core';
import {ResizeObserver}            from "resize-observer";

/**
 *
 * @class StringLineViewerWindow - host for the Three.js based string line viewer for scenario + train
 * movements.
 *
 * @note this class uses dynamic component creation to host an Angular component inside a non-Angular
 * object (i.e. DocumentWindow).
 *
 * @brief window for table based browsing of a scenario
 *
 * - - - - - - - - - - - - - - - - - - -
 * @author    Michael Garvin (mike.garvin@ironroad.ca)
 * @date      June 2020
 *
 */
export class StringLineViewerWindow extends DocumentWindow {

  /** access to the virtual file system */
  private blobber:           BlobService = null;

  /** access to user authentication */
  private auth:    AuthenticationService = null;

  /** access to user authentication */
  private ide:                IdeService = null;

  /** the document manager we are a tab  of */
  private manager: DocumentWindowManager = null;

  /** the current scenario (blob key) */
  private input:                  string = '';

  /** the current result (blob key) - train movement/simulation result */
  private result:                 string = '';

  /** the Angular component that is the actual scenario browser...which we are hosting */
  private viewer: StringViewerComponent = null;

  /** the resizer tracker */
  private resizeObserver: ResizeObserver = null;

  /** how to interpret the input values 'blob' == blob keys, 'direct' == the actual JSON strings */
  private mode: string                   = 'blob';

  /** standard constructor */
  constructor(
       meta: StringLineViewerMetadata,
    manager: DocumentWindowManager
  ) {

    super(manager, meta, manager.layoutManager());

    Logger.debug('[StringLineViewerWindow] constructing...');

    this.manager     = manager;
    this.auth        = this.manager.authService();
    this.blobber     = this.manager.blobService();
    this.ide         = this.manager.ideService();
    this.input       = meta.input;
    this.result      = meta.result;
    this.mode        = meta.mode;

    this.showViewer();
  }

  public close(implied: boolean = false) {

    Logger.debug(`[StringLineViewerWindow] closing!`);

    this.viewer.close();

    this.viewer = null;

    this.resizeObserver.disconnect();

    this.resizeObserver = null;

    super.close(implied);
  }

  /** restore the string line viewer (on current 'focus') */
  public showViewer() {

    let slvw: StringLineViewerWindow = this;

    Logger.debug(`[StringLineViewerWindow] => ${slvw.input} / ${slvw.result} ...`);

    const container: JQuery<HTMLElement> = $(`#${slvw.meta.id}`);

    if (!$(container) || !$(container).is('div')) {

      slvw.unReady();

      Logger.error(`[StringLineViewerWindow] missing hosting container (${slvw.meta.id})!`);

      return ;
    }

    $(container).html(`
<div id="string_line_viewer_${slvw.meta.id}" class="string-line-viewer-wrapper">

</div>
`);

    /*
     * wait for the HTML to actually update with the component selector above, this won't create the
     * component, its just HTML :)  But, we can use Angular's component factory etc. to create the
     * component and effective "host" an Angular component inside our DocumentWindow (which isn't
     * Angular based).
     *
     */

    const checkExist = setInterval(() => {

      const dom: HTMLElement = document.getElementById(`string_line_viewer_${slvw.meta.id}`) as HTMLElement;

      if (dom) {

        clearInterval(checkExist);

        Logger.debug(`[StringLineViewerWindow] inject string line viewer component ...`);

              const    view: ViewContainerRef   = slvw.ide.view;
        const factory: ComponentFactoryResolver = slvw.ide.componentResolver;


        const creator   = factory.resolveComponentFactory(StringViewerComponent);
        const componentRef = view.createComponent(creator);

        slvw.viewer                = componentRef.instance  as StringViewerComponent;

        const domElem = (componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

        /* ensure the new component is parented under our container */

        dom.appendChild(domElem);

        /*
         * add a self watch for changes in wrapper size...because sometime it will change because
         * the surrounding panels have changed in size...not because the browser has changed in size;
         * that is, it's not a window resize event, so that kind of size change wouldn't normally
         * be caught.
         *
         */

        slvw.resizeObserver = new ResizeObserver(entries => {

          if (slvw.viewer.viewer) {

            slvw.viewer.viewer.resize();
          }
        });

        slvw.resizeObserver.observe( dom );

        /*  can now use 'browser' as usual ... */

        /* load the blob if there is on e*/

        if (this.mode == 'blob') {

          slvw.loadBlobKeys(slvw.input, slvw.result);

        } else {

          this.loadStrings(slvw.input, slvw.result);
        }

        slvw.viewer.reloaded = () => {

          slvw.input  = '';
          slvw.result = '';
          slvw.title  = '';
          slvw.mode   = 'blob';

          slvw.updateFocus(slvw.input, slvw.result);

          if (slvw.viewer.scenario) {

            slvw.title = basename(slvw.viewer.fileName);

            if (/\.[^.\/]+$/.test(slvw.title)) {

              slvw.title = slvw.title.substring(0, slvw.title.lastIndexOf('.'));
            }

            Logger.debug(`[StringLineViewerWindow] content changed: ${slvw.viewer.fileName}`);

          } else {

            Logger.debug(`[StringLineViewerWindow] no content.`);
          }

        };
      }

    }, 100);
  }

  /**
   * start up the string line viewer with the content that we have in hand; the text
   * for the scenario file, and the text for the movement file.
   *
   * @param scenario
   * @param movement
   * @private
   */
  private loadStrings(scenario: string, movement: string): void {

    if ((scenario === '') || (movement === '')) {

      /* nothing to do yet */

      return ;
    }

    Logger.debug(`[StringLineViewerWindow] loadStrings - direct string load...`);

    try {

      this.viewer.fromText(scenario, movement, (this.meta as StringLineViewerMetadata).label);

      this.updateFocus(scenario, movement);

    } catch (error) {

      Logger.error(`[StringLineViewerWindow] loadBlobKeys - problem loading movement JSON: `, error);
    }

  }

  /**
   * start up the string line viewer with the content implied by the blob keys we have
   * for the scenario and the movement.
   *
   * @param scenario the input scenario
   * @param movement the train movements from simulation
   *
   * @private
   */
  private loadBlobKeys(scenario: string, movement: string): void {

    if ((scenario === '') || (movement === '')) {

      /* nothing to do yet */

      return ;
    }

    Logger.debug(`[StringLineViewerWindow] loadBlobKeys - scenario=${scenario}, movement=${movement}`);

    this.blobber.contents(scenario)
      .pipe(first())
      .subscribe({
        next: (content: string): void => {

          try {

            Logger.debug(`[StringLineViewerWindow] loadBlobKeys - input from blob (sz=${content.length})...`);

            Logger.debug(`[StringLineViewerWindow] loadBlobKeys - looking for output...`);

            this.blobber.contents(movement)
              .pipe(first())
              .subscribe({
                next: (outputContent: string): void => {

                  try {

                    Logger.debug(`[StringLineViewerWindow] loadBlobKeys - output from blob (sz=${outputContent.length})...`);

                    this.viewer.fromText(content, outputContent, scenario);

                    this.updateFocus(scenario, movement);

                  } catch (error) {

                    Logger.error(`[StringLineViewerWindow] loadBlobKeys - problem loading movement JSON: `, error);
                  }

                },
                error: (err): void => {

                  Logger.error(`[StringLineViewerWindow] loadBlobKeys - can't find movement content ${movement}: `, errorAsText(err));
                }

              });

            } catch (error) {

              Logger.error(`[StringLineViewerWindow] loadBlobKeys - problem loading scenario JSON: `, error);
            }

          },
          error: (err): void => {

            Logger.error(`[StringLineViewerWindow] loadBlobKeys - can't find scenario content ${scenario}: `, errorAsText(err));
          }

        });

  }

  /** update the focus to current */
  private updateFocus(input: string, result: string): void {

    this.input  = input;
    this.result = result;

    let label: string = (this.meta as StringLineViewerMetadata).label;

    if (this.mode == 'blob') {

      /* the title is derived from the label */

      label = this.input;

    }

    let keyBase = label.substring(label.lastIndexOf('/') + 1);

    if (!keyBase || keyBase === '' ) {

      keyBase = label;
    }

    this.title  = `${keyBase}`;

    /* set the title for the window */

    if (/\.[^.\/]+$/.test(this.title)) {

      this.title = this.title.substring(0, this.title.lastIndexOf('.'));
    }

  }

}
